<template>
  <v-card class="tenant-transfer-panel" :class="{ dark: isDark }" flat>
    <section class="transfer-main unity-transfer-main">
      <div class="transfer-head">
        <div class="transfer-title">
          <v-avatar size="34" color="#e8f5ee">
            <v-icon color="#128C7E">mdi-source-branch-sync</v-icon>
          </v-avatar>
          <div>
            <strong>Movimentação entre polos</strong>
            <small>{{ currentUnityName }} · {{ latestUnityMovement ? "última saída registrada" : "sem saída registrada" }}</small>
          </div>
        </div>
        <v-chip small color="#128C7E" dark>
          {{ unityMovements.length }} movimentação{{ unityMovements.length === 1 ? "" : "es" }}
        </v-chip>
      </div>

      <div class="transfer-content movement-history-only">
        <div class="transfer-timeline">
          <div v-if="!unityMovements.length" class="empty-state">
            <v-icon size="30" color="#90a4ae">mdi-map-marker-path</v-icon>
            <span>Nenhuma movimentação entre polos criada.</span>
          </div>
          <article v-for="movement in compactUnityMovements" :key="movement._id" class="movement-row">
            <div class="route-line">
              <strong>{{ unityName(movement.from_unity) }}</strong>
              <v-icon small color="#90a4ae">mdi-arrow-right</v-icon>
              <strong>{{ unityName(movement.to_unity) }}</strong>
              <v-chip x-small color="#128C7E" dark>{{ movement.movement_type_label || movementTypeText(movement.movement_type) }}</v-chip>
            </div>
            <div class="movement-details">
              <span><v-icon x-small>mdi-account-arrow-right-outline</v-icon> {{ userName(movement.responsible_user) }}</span>
              <span><v-icon x-small>mdi-calendar-clock</v-icon> {{ formatDate(movement.sent_at) }}</span>
            </div>
            <div class="event-strip">
              <span v-if="movement.note">{{ movement.note }}</span>
              <v-btn x-small text :color="$theme.primary" @click="$emit('print-exit-note', movement)">
                <v-icon left x-small>mdi-printer-outline</v-icon>
                Nota de saída
              </v-btn>
            </div>
          </article>
        </div>

        <aside class="transfer-actions">
          <div class="action-step">
            <strong>Nova saída</strong>
            <v-autocomplete
              v-model="unityMove.from_unity"
              :items="unityOptions"
              item-text="nome"
              return-object
              label="Polo de origem"
              outlined
              dense
              hide-details
              :loading="loadingUnityRefs"
            />
            <v-autocomplete
              v-model="unityMove.to_unity"
              :items="unityOptions"
              item-text="nome"
              return-object
              label="Polo de destino"
              outlined
              dense
              hide-details
              :loading="loadingUnityRefs"
            />
            <v-select
              v-model="unityMove.movement_type"
              :items="movementTypeOptions"
              item-text="text"
              item-value="value"
              label="Tipo"
              outlined
              dense
              hide-details
            />
            <v-autocomplete
              v-model="unityMove.responsible"
              :items="userOptions"
              item-text="nome"
              return-object
              label="Responsável"
              outlined
              dense
              hide-details
              :loading="loadingUnityRefs"
            />
            <v-textarea
              v-model="unityMove.note"
              label="Observação"
              outlined
              dense
              hide-details
              rows="2"
            />
            <v-btn block color="#128C7E" dark depressed :loading="movingUnity" @click="moveToUnity">
              Registrar e imprimir
            </v-btn>
          </div>
        </aside>
      </div>
    </section>

    <section class="transfer-main">
      <div class="transfer-head">
        <div class="transfer-title">
          <v-avatar size="34" color="#e8f5ee">
            <v-icon color="#128C7E">mdi-swap-horizontal-bold</v-icon>
          </v-avatar>
          <div>
            <strong>Movimentacao entre empresas</strong>
            <small>{{ currentTenantName }} · {{ activeMovement ? movementStatusText(activeMovement.status) : "sem envio ativo" }}</small>
          </div>
        </div>
        <v-chip small :color="activeMovement ? statusColor(activeMovement.status) : '#90a4ae'" dark>
          {{ activeMovement ? movementStatusText(activeMovement.status) : "Disponivel" }}
        </v-chip>
      </div>

      <div class="transfer-content movement-history-only">
        <div class="transfer-timeline">
          <div v-if="activeMovement" class="transfer-stage-bar">
            <span
              v-for="stage in movementStages"
              :key="stage.status"
              :class="{ active: stage.status === activeMovement.status, done: stage.done }"
            >
              <i />
              {{ stage.label }}
            </span>
          </div>
          <div v-if="!tenantMovements.length" class="empty-state">
            <v-icon size="30" color="#90a4ae">mdi-truck-outline</v-icon>
            <span>Nenhuma movimentacao criada. Use o botao da direita para iniciar.</span>
          </div>
          <article v-for="movement in compactMovements" :key="movement._id" class="movement-row">
            <div class="route-line">
              <strong>{{ tenantName(movement.from_tenant) }}</strong>
              <v-icon small color="#90a4ae">mdi-arrow-right</v-icon>
              <strong>{{ tenantName(movement.to_tenant) }}</strong>
              <v-chip x-small :color="statusColor(movement.status)" dark>{{ movementStatusText(movement.status) }}</v-chip>
            </div>
            <div class="movement-details">
              <span><v-icon x-small>mdi-send</v-icon> {{ userName(movement.sender_user) }}</span>
              <span><v-icon x-small>mdi-account-check</v-icon> {{ userName(movement.receiver_user) }}</span>
              <span><v-icon x-small>mdi-truck-fast-outline</v-icon> {{ transporterName(movement) }}</span>
            </div>
            <div class="event-strip">
              <span v-for="event in lastEvents(movement)" :key="event._id">
                {{ movementStatusText(event.type) }} · {{ formatDate(event.at) }}
              </span>
            </div>
          </article>
        </div>

        <aside class="transfer-actions">
          <template v-if="!flowOpen">
            <v-btn block color="#128C7E" dark depressed @click="openFlow">
              Nova movimentacao
            </v-btn>
            <div class="quick-actions">
              <v-btn v-if="nextAction" block small outlined :loading="updating" @click="updateMovement(nextAction.movement, nextAction.status)">
                {{ nextAction.label }}
              </v-btn>
              <v-btn block small text @click="loadTenants">Atualizar parceiras</v-btn>
            </div>
          </template>

          <template v-else>
            <div class="mini-stepper">
              <button :class="{ active: transferStep === 1, done: targetTenantId }" @click="transferStep = 1">1</button>
              <button :class="{ active: transferStep === 2, done: hasTransporter }" :disabled="!targetTenantId" @click="transferStep = 2">2</button>
              <button :class="{ active: transferStep === 3 }" :disabled="!targetTenantId" @click="transferStep = 3">3</button>
            </div>

            <div v-if="transferStep === 1" class="action-step">
              <strong>Escolha a parceira</strong>
              <v-autocomplete
                v-model="targetTenantId"
                :items="tenantOptions"
                :search-input.sync="tenantSearch"
                item-text="nome"
                item-value="_id"
                placeholder="Nome, CNPJ ou telefone"
                outlined
                dense
                hide-details
                :loading="loadingTenants"
                @update:search-input="searchTenants"
              />
            </div>

            <div v-else-if="transferStep === 2" class="action-step">
              <strong>Transporte</strong>
              <v-text-field v-model="transporter.name" placeholder="Quem transportou" outlined dense hide-details />
              <v-text-field v-model="transporter.phone" placeholder="Telefone" outlined dense hide-details />
              <v-text-field v-model="transporter.vehicle" placeholder="Veiculo" outlined dense hide-details />
            </div>

            <div v-else class="action-step">
              <strong>Confirmar envio</strong>
              <v-textarea v-model="transferNote" placeholder="Observacao curta" outlined dense hide-details rows="2" />
              <small>Ao enviar, a tenant parceira passa a ver esta mesma venda.</small>
            </div>

            <div class="flow-buttons">
              <v-btn small text @click="closeFlow">Cancelar</v-btn>
              <v-btn v-if="transferStep > 1" small text @click="transferStep -= 1">Voltar</v-btn>
              <v-btn v-if="transferStep < 3" small color="#128C7E" dark :disabled="!canAdvance" @click="transferStep += 1">Avancar</v-btn>
              <v-btn v-else small color="#25D366" dark :loading="sending" :disabled="!targetTenantId" @click="sendToTenant">Enviar</v-btn>
            </div>
          </template>
        </aside>
      </div>
    </section>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "SaleTenantMovementPanel",
  data() {
    return {
      flowOpen: false,
      transferStep: 1,
      tenantOptions: [],
      tenantSearch: "",
      targetTenantId: "",
      transferNote: "",
      transporter: {
        name: "",
        phone: "",
        vehicle: ""
      },
      loadingTenants: false,
      sending: false,
      updating: false,
      searchTimer: null,
      unityOptions: [],
      userOptions: [],
      loadingUnityRefs: false,
      movingUnity: false,
      selectedExitMovement: null,
      unityMove: {
        from_unity: null,
        to_unity: null,
        movement_type: "physical_piece",
        responsible: null,
        note: ""
      },
      movementTypeOptions: [
        { text: "Peça física", value: "physical_piece" },
        { text: "Responsabilidade de montagem", value: "assembly" }
      ]
    };
  },
  computed: {
    ...mapGetters(["get_gestordepedido", "getLoggedUser"]),
    sale() {
      return this.get_gestordepedido || {};
    },
    movements() {
      return this.sale.tenant_movements || [];
    },
    unityMovements() {
      return this.movements.filter(item => item.movement_scope === "unity");
    },
    tenantMovements() {
      return this.movements.filter(item => item.movement_scope !== "unity");
    },
    compactUnityMovements() {
      return [...this.unityMovements].reverse().slice(0, 3);
    },
    compactMovements() {
      return [...this.tenantMovements].reverse().slice(0, 3);
    },
    activeMovement() {
      return [...this.tenantMovements].reverse().find(item => !["completed", "canceled"].includes(item.status));
    },
    latestUnityMovement() {
      return this.compactUnityMovements[0] || null;
    },
    isDark() {
      return localStorage.dark === "true";
    },
    currentTenantId() {
      return String(this.getLoggedUser?.tenant?.[0]?._id || this.getLoggedUser?.activeTenant?._id || "");
    },
    currentTenantName() {
      return this.sale.current_tenant?.nome ||
        this.tenantName(this.activeMovement?.to_tenant) ||
        this.getLoggedUser?.tenant?.[0]?.nome ||
        "Tenant atual";
    },
    currentUnityName() {
      return this.sale.current_unity?.nome ||
        this.sale.production_unity?.nome ||
        this.sale.unity?.nome ||
        "Polo atual não definido";
    },
    hasTransporter() {
      return Boolean(this.transporter.name || this.transporter.phone || this.transporter.vehicle);
    },
    canAdvance() {
      if (this.transferStep === 1) return Boolean(this.targetTenantId);
      return true;
    },
    nextAction() {
      const movement = this.activeMovement;
      if (!movement) return null;
      if (this.canMarkInTransit(movement)) return { movement, status: "in_transit", label: "Marcar em transporte" };
      if (this.canReceive(movement)) return { movement, status: "received", label: "Receber venda" };
      if (this.canReturn(movement)) return { movement, status: "returning", label: "Devolver venda" };
      if (this.canConfirmReturned(movement)) return { movement, status: "returned", label: "Confirmar retorno" };
      if (this.canComplete(movement)) return { movement, status: "completed", label: "Finalizar fluxo" };
      return null;
    },
    movementStages() {
      const order = ["sent", "in_transit", "received", "returning", "returned", "completed"];
      const activeIndex = order.indexOf(this.activeMovement?.status);
      return order.map((status, index) => ({
        status,
        label: this.movementStatusText(status),
        done: activeIndex > index
      }));
    }
  },
  methods: {
    ...mapActions(["createGlobalMessage"]),
    async loadUnityRefs() {
      this.loadingUnityRefs = true;
      try {
        const [unities, users] = await Promise.all([
          this.$run("unidades/list", { limitPerPage: 2000 }),
          this.$run("funcionarios/list", { limitPerPage: 2000 })
        ]);
        this.unityOptions = unities.data.docs || [];
        this.userOptions = users.data.docs || [];
        this.prefillUnityMove();
      } finally {
        this.loadingUnityRefs = false;
      }
    },
    prefillUnityMove() {
      const origin = this.sale.current_unity || this.sale.production_unity || this.sale.unity || null;
      const originId = origin && (origin._id || origin.id || origin);
      this.unityMove.from_unity = this.unityOptions.find(item => String(item._id) === String(originId)) || origin || null;
    },
    movementTypeText(type) {
      return type === "assembly" ? "Responsabilidade de montagem" : "Peça física";
    },
    unityName(unity) {
      return unity?.nome || "Polo";
    },
    async moveToUnity() {
      if (!this.sale._id) return;
      if (!this.unityMove.from_unity || !this.unityMove.to_unity || !this.unityMove.responsible) {
        this.createGlobalMessage({ type: "error", message: "Informe origem, destino e responsável.", timeout: 4500 });
        return;
      }
      this.movingUnity = true;
      try {
        const response = await this.$run("transacoes/move-to-unity", {
          _id: this.sale._id,
          from_unity: this.unityMove.from_unity,
          to_unity: this.unityMove.to_unity,
          movement_type: this.unityMove.movement_type,
          responsible: this.unityMove.responsible,
          note: this.unityMove.note
        });
        this.applySale(response.data.sale || response.data);
        this.selectedExitMovement = response.data.movement;
        this.createGlobalMessage({ type: "success", message: "Movimentação entre polos registrada.", timeout: 3500 });
        await this.$nextTick();
        this.printExitNote(this.selectedExitMovement);
        this.unityMove = {
          from_unity: response.data.movement && response.data.movement.to_unity,
          to_unity: null,
          movement_type: "physical_piece",
          responsible: null,
          note: ""
        };
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: error.response?.data?.message || "Não foi possível mover a peça.",
          timeout: 5000
        });
      } finally {
        this.movingUnity = false;
      }
    },
    printExitNote(movement) {
      this.selectedExitMovement = movement;
      this.$nextTick(() => {
        const printNode = document.getElementById("print-exit-note");
        if (!printNode) {
          this.createGlobalMessage({ type: "error", message: "Nota de saída não encontrada.", timeout: 4000 });
          return;
        }
        const windowPrint = window.open("", "", "width=800,height=700");
        setTimeout(() => {
          windowPrint.document.open();
          windowPrint.document.write("<html><head><title>Nota de saída</title>");
          windowPrint.document.write('<meta charset="utf-8">');
          windowPrint.document.write("<style>body { -webkit-print-color-adjust: exact; font-family: Arial, sans-serif; } @page { size: A4; margin: 0.7cm; }</style>");
          windowPrint.document.write("</head><body>");
          windowPrint.document.write(printNode.innerHTML);
          windowPrint.document.write("</body></html>");
          windowPrint.document.close();
          windowPrint.focus();
          windowPrint.onload = () => {
            windowPrint.print();
            if (this.$vuetify.breakpoint.lgAndUp) windowPrint.close();
          };
        }, 150);
      });
    },
    openFlow() {
      this.flowOpen = true;
      this.transferStep = 1;
      this.loadTenants();
    },
    closeFlow() {
      this.flowOpen = false;
      this.transferStep = 1;
      this.targetTenantId = "";
      this.transferNote = "";
    },
    searchTenants() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(this.loadTenants, 350);
    },
    async loadTenants() {
      this.loadingTenants = true;
      try {
        const response = await this.$run("transacoes/search-tenant-partners", {
          search: this.tenantSearch || ""
        });
        this.tenantOptions = response.data.docs || [];
      } finally {
        this.loadingTenants = false;
      }
    },
    async sendToTenant() {
      if (!this.sale._id || !this.targetTenantId) return;
      this.sending = true;
      try {
        const response = await this.$run("transacoes/send-to-tenant", {
          _id: this.sale._id,
          to_tenant_id: this.targetTenantId,
          transporter_name: this.transporter.name,
          transporter_phone: this.transporter.phone,
          transporter_vehicle: this.transporter.vehicle,
          note: this.transferNote
        });
        this.applySale(response.data);
        this.closeFlow();
        this.createGlobalMessage({ type: "success", message: "Venda enviada para a tenant parceira.", timeout: 3500 });
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: error.response?.data?.message || "Nao foi possivel enviar a venda.",
          timeout: 5000
        });
      } finally {
        this.sending = false;
      }
    },
    async updateMovement(movement, status) {
      this.updating = true;
      try {
        const response = await this.$run("transacoes/update-tenant-movement", {
          _id: this.sale._id,
          movement_id: movement._id,
          status,
          transporter_name: this.transporter.name,
          transporter_phone: this.transporter.phone,
          transporter_vehicle: this.transporter.vehicle
        });
        this.applySale(response.data);
        this.createGlobalMessage({ type: "success", message: "Movimentacao atualizada.", timeout: 3000 });
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: error.response?.data?.message || "Nao foi possivel atualizar a movimentacao.",
          timeout: 5000
        });
      } finally {
        this.updating = false;
      }
    },
    applySale(sale) {
      this.$store.commit("set_gestordepedido", sale);
    },
    handleSaleUpdated(event) {
      const payload = event.detail || {};
      if (String(payload.sale_id) === String(this.sale._id) && payload.sale) {
        this.applySale(payload.sale);
      }
    },
    lastEvents(movement) {
      return [...(movement.events || [])].slice(-2);
    },
    tenantName(tenant) {
      return tenant?.nome || "Tenant";
    },
    userName(user) {
      return user?.nome || "Pendente";
    },
    transporterName(movement) {
      return movement?.transporter?.name || "Nao informado";
    },
    isFromTenant(movement) {
      return String(movement?.from_tenant?._id || "") === this.currentTenantId;
    },
    isToTenant(movement) {
      return String(movement?.to_tenant?._id || "") === this.currentTenantId;
    },
    canMarkInTransit(movement) {
      return this.isFromTenant(movement) && movement.status === "sent";
    },
    canReceive(movement) {
      return this.isToTenant(movement) && ["sent", "in_transit"].includes(movement.status);
    },
    canReturn(movement) {
      return this.isToTenant(movement) && movement.status === "received";
    },
    canConfirmReturned(movement) {
      return this.isFromTenant(movement) && movement.status === "returning";
    },
    canComplete(movement) {
      return this.isFromTenant(movement) && movement.status === "returned";
    },
    movementStatusText(status) {
      const labels = {
        sent: "Enviada",
        in_transit: "Em transporte",
        received: "Recebida",
        returning: "Voltando",
        returned: "Devolvida",
        completed: "Finalizada",
        canceled: "Cancelada"
      };
      return labels[status] || "Movimentacao";
    },
    statusColor(status) {
      const colors = {
        sent: "#1976d2",
        in_transit: "#fb8c00",
        received: "#25D366",
        returning: "#8e24aa",
        returned: "#00acc1",
        completed: "#43a047",
        canceled: "#e53935"
      };
      return colors[status] || "#90a4ae";
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD/MM HH:mm") : "";
    }
  },
  mounted() {
    window.addEventListener("stonepro:sale-updated", this.handleSaleUpdated);
    this.loadUnityRefs();
  },
  beforeDestroy() {
    window.clearTimeout(this.searchTimer);
    window.removeEventListener("stonepro:sale-updated", this.handleSaleUpdated);
  }
};
</script>

<style scoped>
.tenant-transfer-panel {
  background: transparent;
  border-bottom: 0;
  color: #1f2f2a;
  padding: 8px 0 12px;
}
.transfer-main {
  background: linear-gradient(135deg, #ffffff, #f4fbf7);
  border: 1px solid #dfece6;
  border-radius: 24px;
  box-shadow: 0 16px 40px rgba(18, 140, 126, 0.08);
  overflow: hidden;
}
.unity-transfer-main {
  margin-bottom: 12px;
}
.transfer-head {
  align-items: center;
  background: linear-gradient(135deg, #eefaf3, #ffffff);
  border-bottom: 1px solid #dfece6;
  display: flex;
  justify-content: space-between;
  min-height: 56px;
  padding: 12px 16px;
}
.transfer-title {
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
}
.transfer-title strong,
.transfer-title small {
  display: block;
}
.transfer-title small {
  color: #667781;
  font-size: 12px;
}
.transfer-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 286px;
  min-height: 214px;
}
.movement-history-only {
  grid-template-columns: minmax(0, 1fr);
}
.movement-history-only > .transfer-actions {
  display: none;
}
.transfer-timeline {
  max-height: 270px;
  overflow: auto;
  padding: 12px 14px;
}
.transfer-actions {
  background: #f4fbf7;
  border-left: 1px solid #dfece6;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: flex-start;
  padding: 14px;
}
.transfer-stage-bar {
  background: #ffffff;
  border: 1px solid #dfece6;
  border-radius: 18px;
  display: grid;
  gap: 7px;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin-bottom: 10px;
  padding: 9px;
}
.transfer-stage-bar span {
  align-items: center;
  color: #667781;
  display: flex;
  flex-direction: column;
  font-size: 10px;
  font-weight: 800;
  gap: 4px;
  min-width: 0;
  text-align: center;
}
.transfer-stage-bar i {
  background: #d8e7df;
  border-radius: 999px;
  display: block;
  height: 8px;
  width: 100%;
}
.transfer-stage-bar span.done i,
.transfer-stage-bar span.active i {
  background: linear-gradient(90deg, #128C7E, #25D366);
}
.transfer-stage-bar span.active {
  color: #128C7E;
}
.empty-state {
  align-items: center;
  color: #667781;
  display: flex;
  gap: 10px;
  height: 150px;
  justify-content: center;
  text-align: center;
}
.movement-row {
  background: #ffffff;
  border: 1px solid #dfece6;
  border-radius: 20px;
  box-shadow: 0 10px 24px rgba(18, 140, 126, 0.06);
  margin-bottom: 10px;
  padding: 11px 12px;
}
.route-line,
.movement-details,
.event-strip,
.quick-actions,
.flow-buttons {
  align-items: center;
  display: flex;
  gap: 8px;
}
.route-line {
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.route-line strong {
  background: rgba(18, 140, 126, 0.09);
  border: 1px solid rgba(18, 140, 126, 0.16);
  border-radius: 999px;
  color: #075e54;
  font-size: 12px;
  padding: 5px 9px;
}
.movement-details {
  color: #667781;
  flex-wrap: wrap;
  font-size: 12px;
  margin-bottom: 6px;
}
.movement-details span,
.event-strip span {
  align-items: center;
  display: inline-flex;
  gap: 4px;
}
.event-strip {
  color: #7b9087;
  flex-wrap: wrap;
  font-size: 11px;
}
.quick-actions,
.flow-buttons {
  flex-direction: column;
}
.mini-stepper {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, 1fr);
}
.mini-stepper button {
  background: #ffffff;
  border: 1px solid #dfece6;
  border-radius: 999px;
  color: #667781;
  height: 30px;
  font-weight: 800;
}
.mini-stepper button.active {
  background: #128C7E;
  color: #ffffff;
}
.mini-stepper button.done {
  border-color: #25D366;
}
.action-step {
  display: grid;
  gap: 8px;
}
.action-step strong {
  font-size: 13px;
}
.action-step small {
  color: #667781;
  line-height: 1.35;
}
.tenant-transfer-panel.dark {
  background: transparent !important;
  border-color: transparent;
}
.tenant-transfer-panel.dark .transfer-main {
  background: linear-gradient(135deg, #111b21, #0b141a);
  border-color: #26343c;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
}
.tenant-transfer-panel.dark .transfer-head,
.tenant-transfer-panel.dark .movement-row,
.tenant-transfer-panel.dark .mini-stepper button,
.tenant-transfer-panel.dark .transfer-stage-bar {
  background: rgba(24, 34, 41, 0.86);
  border-color: #26343c;
}
.tenant-transfer-panel.dark .transfer-actions {
  background: #111b21;
  border-color: #26343c;
}
.tenant-transfer-panel.dark .transfer-title strong,
.tenant-transfer-panel.dark .route-line strong,
.tenant-transfer-panel.dark .action-step strong {
  color: #eef4f1;
}
.tenant-transfer-panel.dark .route-line strong {
  background: rgba(37, 211, 102, 0.08);
  border-color: rgba(37, 211, 102, 0.14);
}
.tenant-transfer-panel.dark .transfer-title small,
.tenant-transfer-panel.dark .movement-details,
.tenant-transfer-panel.dark .event-strip,
.tenant-transfer-panel.dark .empty-state,
.tenant-transfer-panel.dark .action-step small,
.tenant-transfer-panel.dark .transfer-stage-bar span {
  color: #8ea39a;
}
.tenant-transfer-panel.dark .transfer-stage-bar i {
  background: #26343c;
}
.tenant-transfer-panel.dark .transfer-stage-bar span.active {
  color: #9be7bd;
}
.tenant-transfer-panel.dark .mini-stepper button {
  color: #b7d4c7;
}
@media(max-width: 1100px) {
  .transfer-content {
    grid-template-columns: 1fr;
  }
  .transfer-actions {
    border-left: 0;
    border-top: 1px solid #e0d4ba;
  }
}
</style>
